<template>
  <Layout>
    <div class="personal-profile">
      <div
        class="profile-page-heading d-flex flex-wrap justify-content-between align-items-center mb-4"
      >
        <div>
          <p class="profile-eyebrow mb-1">
            {{ $t("label.profilePersonalSpace") }}
          </p>
          <h3 class="mb-0">{{ $t("label.profileMyProfile") }}</h3>
        </div>
        <b-button
          variant="link"
          class="profile-refresh px-0"
          :disabled="loading"
          @click="loadProfile"
          ><i class="mdi mdi-refresh mr-1"></i
          >{{ $t("label.refresh") }}</b-button
        >
      </div>
      <div v-if="loading" class="profile-loading text-center p-5">
        <b-spinner variant="info" />
        <p class="text-muted mt-3 mb-0">{{ $t("label.profileLoading") }}</p>
      </div>
      <b-alert v-else-if="error" show variant="danger"
        >{{ error }}
        <b-button size="sm" variant="outline-danger" @click="loadProfile">{{
          $t("label.retryPermissionDefinitions")
        }}</b-button></b-alert
      >
      <template v-else-if="profile">
        <section class="profile-hero mb-4">
          <div class="profile-hero-decoration" aria-hidden="true"></div>
          <div class="profile-hero-content">
            <div class="profile-portrait">
              <img
                v-if="profile.image && !imageError"
                :src="profile.image"
                :alt="profile.name"
                @error="imageError = true"
              />
              <span v-else>{{ profile.initials }}</span>
            </div>
            <div class="profile-identity">
              <h1>{{ profile.name }}</h1>
              <p class="profile-position mb-1">
                {{
                  primaryCompany
                    ? primaryCompany.position
                    : $t("label.profileWelcome")
                }}
              </p>
              <p v-if="primaryCompany" class="profile-company mb-0">
                <i class="mdi mdi-domain mr-1" aria-hidden="true"></i
                ><CompanyLink :uuid="primaryCompany.companyUUID" :name="primaryCompany.company" inherit-color />
              </p>
            </div>
            <span class="profile-account-badge"
              ><span
                :class="
                  profile.isActive
                    ? 'profile-dot-active'
                    : 'profile-dot-inactive'
                "
              ></span
              >{{
                $t(
                  profile.isActive
                    ? "label.profileActiveAccount"
                    : "label.profileInactiveAccount",
                )
              }}</span
            >
          </div>
        </section>
        <nav
          class="profile-tabs mb-4"
          :aria-label="$t('label.profileSections')"
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            :class="{ active: activeTab === tab.key }"
            :aria-current="activeTab === tab.key ? 'page' : null"
            :aria-controls="`profile-panel-${tab.key}`"
            @click="activeTab = tab.key"
          >
            <i :class="['mdi', tab.icon, 'mr-2']" aria-hidden="true"></i
            >{{ $t(tab.label) }}
          </button>
        </nav>
        <section
          v-if="activeTab === 'personal'"
          id="profile-panel-personal"
          class="row"
        >
          <div class="col-12 col-lg-5">
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i class="mdi mdi-account-outline" aria-hidden="true"></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileAboutMe") }}</h5>
                  <p>{{ $t("label.profileAboutHint") }}</p>
                </div>
              </header>
              <dl class="profile-about-grid mb-0">
                <div v-for="item in aboutRows" :key="item.label">
                  <dt>{{ $t(item.label) }}</dt>
                  <dd>{{ item.value }}</dd>
                </div>
              </dl>
            </article>
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i class="mdi mdi-at" aria-hidden="true"></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileContact") }}</h5>
                  <p>{{ $t("label.profileContactHint") }}</p>
                </div>
              </header>
              <div
                v-for="item in contactRows"
                :key="item.label"
                class="profile-contact-row"
              >
                <i
                  :class="['mdi', item.icon, 'profile-contact-icon']"
                  aria-hidden="true"
                ></i>
                <div class="flex-grow-1">
                  <span class="profile-field-label">{{ $t(item.label) }}</span>
                  <p class="profile-contact-value mb-0">{{ item.value }}</p>
                </div>
                <span v-if="item.verified" class="profile-verified"
                  ><i class="mdi mdi-check-circle mr-1" aria-hidden="true"></i
                  >{{ $t("label.profileVerified") }}</span
                >
              </div>
            </article>
          </div>
          <div class="col-12 col-lg-7">
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i class="mdi mdi-office-building" aria-hidden="true"></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileWorkplace") }}</h5>
                  <p>{{ $t("label.profileWorkplaceHint") }}</p>
                </div>
              </header>
              <div
                v-for="(company, index) in profile.companies"
                :key="company.uuid || index"
                class="profile-workplace-card"
              >
                <div
                  class="d-flex flex-wrap justify-content-between align-items-start mb-3"
                >
                  <div class="profile-workplace-title">
                    <span class="profile-company-monogram" aria-hidden="true">{{
                      company.company.slice(0, 1).toUpperCase()
                    }}</span>
                    <h6><CompanyLink :uuid="company.companyUUID" :name="company.company" /></h6>
                  </div>
                  <span
                    v-if="company.sequence === 1 || company.sequence === '1'"
                    class="profile-primary-badge"
                    >{{ $t("label.profilePrimary") }}</span
                  >
                </div>
                <dl class="profile-workplace-grid mb-0">
                  <div>
                    <dt>{{ $t("module.position") }}</dt>
                    <dd>{{ company.position }}</dd>
                  </div>
                  <div>
                    <dt>{{ $t("module.department") }}</dt>
                    <dd>{{ company.department }}</dd>
                  </div>
                  <div>
                    <dt>{{ $t("module.office") }}</dt>
                    <dd>{{ company.office }}</dd>
                  </div>
                </dl>
              </div>
              <p v-if="!profile.companies.length" class="profile-empty mb-0">
                {{ $t("label.profileNoWorkplace") }}
              </p>
            </article>
          </div>
        </section>
        <section v-else id="profile-panel-account" class="row">
          <div class="col-12 col-lg-7">
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i
                    class="mdi mdi-shield-check-outline"
                    aria-hidden="true"
                  ></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileAccountAccess") }}</h5>
                  <p>{{ $t("label.profileAccountHint") }}</p>
                </div>
              </header>
              <div class="row">
                <div
                  v-for="channel in channels"
                  :key="channel.name"
                  class="col-12 col-md-6 mb-3"
                >
                  <div class="profile-access-card">
                    <div
                      class="d-flex justify-content-between align-items-center mb-4"
                    >
                      <i
                        :class="['mdi', channel.icon, 'profile-channel-icon']"
                        aria-hidden="true"
                      ></i
                      ><b-badge
                        :variant="
                          channel.access === 1
                            ? 'success'
                            : channel.access === 2
                            ? 'warning'
                            : 'secondary'
                        "
                        >{{ accessLabel(channel.access) }}</b-badge
                      >
                    </div>
                    <h5>{{ channel.name }}</h5>
                    <span class="profile-field-label">{{
                      $t("label.profileLastLogin")
                    }}</span>
                    <p class="mb-0 mt-1">{{ channel.lastLogin }}</p>
                  </div>
                </div>
              </div>
            </article>
          </div>
          <div class="col-12 col-lg-5">
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i class="mdi mdi-check-decagram" aria-hidden="true"></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileVerification") }}</h5>
                  <p>{{ $t("label.profileVerificationHint") }}</p>
                </div>
              </header>
              <div
                v-for="item in contactRows"
                :key="item.label"
                class="profile-verification-row"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <strong>{{ $t(item.label) }}</strong
                  ><span
                    :class="item.verified ? 'profile-verified' : 'text-muted'"
                    >{{
                      $t(
                        item.verified
                          ? "label.profileVerified"
                          : "label.profileNotVerified",
                      )
                    }}</span
                  >
                </div>
                <p class="small text-muted mt-2 mb-0">{{ item.date }}</p>
              </div>
            </article>
          </div>
          <div class="col-12">
            <article class="profile-panel mb-4">
              <header class="profile-panel-heading">
                <span class="profile-panel-icon"
                  ><i class="mdi mdi-lock-outline" aria-hidden="true"></i
                ></span>
                <div>
                  <h5>{{ $t("label.profileChangePassword") }}</h5>
                  <p>{{ $t("label.profilePasswordHint") }}</p>
                </div>
              </header>
              <ChangePassword :key="ownUUID" />
            </article>
          </div>
        </section>
      </template>
    </div>
  </Layout>
</template>
<script>
import Layout from "@/views/layouts/main";
import ChangePassword from "@/components/profile/ChangePassword.vue";
import i18n from "@/i18n";
import { userService } from "@/services/api/user.service";
import { userDetailsModel } from "@/models/api/userDetails.model";
import { sharedHelper } from "@/utils";
export default {
  name: "PersonalProfile",
  page: { title: i18n.t("module.profile") },
  components: { Layout, ChangePassword },
  data() {
    return {
      profile: null,
      loading: true,
      error: "",
      imageError: false,
      activeTab: "personal",
      requestId: 0,
      tabs: [
        {
          key: "personal",
          label: "label.profileOverview",
          icon: "mdi-account-outline",
        },
        {
          key: "account",
          label: "label.profileAccount",
          icon: "mdi-shield-check-outline",
        },
      ],
    };
  },
  computed: {
    ownUUID() {
      return this.$store.state.api.auth.user?.user?.uuid || "";
    },
    primaryCompany() {
      return (
        this.profile?.companies.find(
          (company) => Number(company.sequence) === 1,
        ) ||
        this.profile?.companies[0] ||
        null
      );
    },
    aboutRows() {
      return [
        { label: "label.userDetailsFirstName", value: this.profile.firstName },
        { label: "label.userDetailsLastName", value: this.profile.lastName },
        { label: "label.userDetailsBirthDate", value: this.profile.birthDate },
      ];
    },
    contactRows() {
      return [
        {
          label: "label.email",
          icon: "mdi-email-outline",
          value: this.profile.email,
          verified: this.profile.emailVerifiedAt !== "-",
          date: this.profile.emailVerifiedAt,
        },
        {
          label: "label.phone",
          icon: "mdi-phone-outline",
          value: this.profile.phone,
          verified: this.profile.phoneVerifiedAt !== "-",
          date: this.profile.phoneVerifiedAt,
        },
      ];
    },
    channels() {
      return [
        {
          name: "iHextar App",
          icon: "mdi-cellphone",
          access: this.profile.appAccess,
          lastLogin: this.profile.appLastLogin,
        },
        {
          name: "Portal",
          icon: "mdi-monitor",
          access: this.profile.portalAccess,
          lastLogin: this.profile.portalLastLogin,
        },
      ];
    },
  },
  watch: {
    ownUUID() {
      this.activeTab = "personal";
      this.loadProfile();
    },
  },
  created() {
    this.loadProfile();
  },
  beforeDestroy() {
    this.requestId++;
  },
  methods: {
    accessLabel(value) {
      return value === 1
        ? this.$t("label.dashboardAccessible")
        : value === 2
        ? this.$t("label.userDetailsPending")
        : value === 0
        ? this.$t("label.dashboardNoAccess")
        : "-";
    },
    async loadProfile() {
      const request = ++this.requestId,
        uuid = this.ownUUID;
      this.loading = true;
      this.error = "";
      this.profile = null;
      this.imageError = false;
      if (!uuid) {
        this.error = this.$t("label.profileNoSession");
        this.loading = false;
        return;
      }
      try {
        const result = await userService.show(uuid);
        if (!result?.success || result.data?.uuid !== uuid)
          throw new Error(this.$t("label.profileLoadError"));
        if (request !== this.requestId) return;
        this.profile = userDetailsModel(result.data);
      } catch (error) {
        if (request === this.requestId)
          this.error =
            sharedHelper.getErrorMessage(error) ||
            this.$t("label.profileLoadError");
      } finally {
        if (request === this.requestId) this.loading = false;
      }
    },
  },
};
</script>
<style scoped>
.personal-profile {
  max-width: 1220px;
  margin: 0 auto;
  color: #22333b;
}
.profile-eyebrow,
.profile-hero-eyebrow {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.8px;
  text-transform: uppercase;
}
.profile-eyebrow {
  color: #648078;
}
.profile-page-heading h3 {
  font-size: 26px;
  letter-spacing: -0.6px;
  font-weight: 700;
}
.profile-refresh {
  color: #3c776a;
  font-weight: 600;
}
.profile-loading,
.profile-panel {
  background: #fff;
  border: 1px solid #e7eceb;
  border-radius: 18px;
}
.profile-hero {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  background: #143f3b;
  color: #fff;
}
.profile-hero::before {
  content: "";
  position: absolute;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  right: -70px;
  top: -265px;
  border: 60px solid rgba(190, 224, 205, 0.08);
}
.profile-hero-decoration {
  position: absolute;
  width: 270px;
  height: 270px;
  border: 1px solid rgba(190, 224, 205, 0.2);
  border-radius: 50%;
  right: 40px;
  bottom: -210px;
}
.profile-hero-content {
  position: relative;
  display: flex;
  align-items: center;
  gap: 26px;
  padding: 44px 38px;
}
.profile-portrait {
  flex: 0 0 110px;
  width: 110px;
  height: 110px;
  padding: 5px;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
}
.profile-portrait img,
.profile-portrait span {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
}
.profile-portrait span {
  font-size: 32px;
  color: #143f3b;
  background: #d9ebdf;
  font-weight: 700;
}
.profile-identity {
  min-width: 0;
  flex: 1;
}
.profile-hero-eyebrow {
  color: #b2d2c4;
}
.profile-identity h1 {
  font-size: clamp(24px, 3vw, 36px);
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.8px;
  margin-bottom: 10px;
  overflow-wrap: anywhere;
}
.profile-position {
  color: #eff8f2;
  font-size: 15px;
}
.profile-company {
  color: #bdd4ca;
  font-size: 13px;
}
.profile-account-badge {
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 30px;
  font-size: 12px;
  white-space: nowrap;
}
.profile-account-badge span {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}
.profile-dot-active {
  background: #a7d8b9;
}
.profile-dot-inactive {
  background: #bdc6c2;
}
.profile-tabs {
  display: flex;
  gap: 28px;
  border-bottom: 1px solid #dce5e1;
}
.profile-tabs button {
  background: none;
  border: 0;
  border-bottom: 3px solid transparent;
  padding: 12px 2px 15px;
  color: #73847f;
  font-weight: 600;
}
.profile-tabs button.active {
  color: #245c50;
  border-bottom-color: #245c50;
}
.profile-tabs button:focus-visible {
  outline: 2px solid #56a48d;
  outline-offset: 3px;
}
.profile-panel {
  padding: 26px;
  height: auto;
}
.profile-panel-heading {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 26px;
}
.profile-panel-heading h5 {
  font-size: 16px;
  margin: 0 0 4px;
  font-weight: 700;
  color: #223c35;
}
.profile-panel-heading p {
  font-size: 12px;
  color: #85938d;
  margin: 0;
}
.profile-panel-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 38px;
  height: 38px;
  border-radius: 12px;
  background: #eef5ef;
  color: #56836a;
  font-size: 20px;
}
.profile-about-grid,
.profile-workplace-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px;
}
.profile-about-grid dt,
.profile-workplace-grid dt,
.profile-field-label {
  font-size: 11px;
  color: #829089;
  font-weight: 500;
}
.profile-about-grid dd,
.profile-workplace-grid dd {
  font-size: 14px;
  font-weight: 500;
  margin: 6px 0 0;
  overflow-wrap: anywhere;
}
.profile-contact-row {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 18px 0;
  border-top: 1px solid #edf1ee;
}
.profile-contact-icon {
  font-size: 20px;
  color: #8b9d94;
}
.profile-contact-row .flex-grow-1 {
  min-width: 0;
}
.profile-contact-value {
  font-size: 13px;
  overflow-wrap: anywhere;
}
.profile-verified {
  color: #417f60;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.profile-workplace-card {
  padding: 22px;
  border: 1px solid #e6ede7;
  border-radius: 14px;
  margin-bottom: 16px;
}
.profile-workplace-card:last-child {
  margin-bottom: 0;
}
.profile-workplace-title {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.profile-workplace-title h6 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  overflow-wrap: anywhere;
}
.profile-company-monogram {
  flex: 0 0 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #edf2ed;
  color: #52745c;
  font-weight: 700;
}
.profile-primary-badge {
  padding: 4px 8px;
  border-radius: 6px;
  background: #f1f5e9;
  color: #78904f;
  font-size: 10px;
  margin: 4px 0 4px 12px;
}
.profile-empty {
  padding: 20px;
  border-radius: 12px;
  background: #f6f8f5;
  color: #7e8e83;
}
.profile-access-card {
  padding: 22px;
  border: 1px solid #e3eae5;
  border-radius: 14px;
  background: #fbfcfa;
  height: 100%;
}
.profile-access-card h5 {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 20px;
}
.profile-channel-icon {
  color: #527764;
  font-size: 26px;
}
.profile-access-card p {
  font-size: 13px;
}
.profile-verification-row {
  padding: 20px 0;
  border-top: 1px solid #edf1ee;
}
.profile-verification-row strong {
  font-size: 13px;
}
@media (max-width: 767px) {
  .profile-hero-content {
    padding: 28px 24px;
    flex-wrap: wrap;
    gap: 20px;
  }
  .profile-portrait {
    width: 88px;
    height: 88px;
    flex-basis: 88px;
  }
  .profile-account-badge {
    margin-left: 108px;
  }
  .profile-panel {
    padding: 22px;
  }
  .profile-identity h1 {
    font-size: 26px;
  }
}
@media (max-width: 420px) {
  .profile-hero-content {
    flex-direction: column;
    align-items: flex-start;
  }
  .profile-account-badge {
    margin-left: 0;
  }
  .profile-contact-row {
    flex-wrap: wrap;
  }
  .profile-verified {
    margin-left: 32px;
  }
  .profile-workplace-card {
    padding: 16px;
  }
  .profile-workplace-grid {
    grid-template-columns: 1fr;
  }
}
</style>
